import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles/PaperlessService.module.scss';

// Context.
import { OmaLummeApp } from '../../omaLummeContext';

// Components.
import Switch from '../Common/Switch';
import Snackbar from '../Common/Snackbar';
import Button from '../Common/Button';
import Dialog from '../Common/Dialog';
import Loading from '../Common/Loading';

// Actions.
import { postCustomerDetails, postProductChange } from '../../actions';
// Types.
import { Contract } from '../../types/contract';
// Utils.
import { validateEmail } from '../../utils/contact';

type PaperlessProps = {
  contract: Contract;
  currentContactMethod: string;
  disabled?: boolean;
  setDisabled?: {
    setEligible: (value: React.SetStateAction<boolean>) => void;
    setReason: (value: React.SetStateAction<string>) => void;
  };
};

/**
 * The title for and internals of a contract dropdown.
 */
const PaperlessService: React.FunctionComponent<PaperlessProps> = (props: PaperlessProps) => {
  const { t } = useTranslation();
  const { contract, currentContactMethod, disabled } = props;
  const setEligible = props.setDisabled?.setEligible;
  const setReason = props.setDisabled?.setReason;

  const { users } = OmaLummeApp.useState();

  const [paperlessService, setPaperlessService] = React.useState<boolean>(currentContactMethod === 'Electronic' ? true : false);
  const [showPaperlessServiceModal, setShowPaperlessServiceModal] = React.useState<boolean>(false);
  const [paperlessEmailAddress, setPaperlessEmailAddress] = React.useState<string | undefined>(users.activeUser.userEmail);
  const [paperlessEmailAddressIsValid, setPaperlessEmailAddressIsValid] = React.useState<boolean | undefined>(undefined);
  const [paperlessSubmitting, setPaperlessSubmitting] = React.useState<boolean>(false);
  const [paperlessSubmitSuccess, setPaperlessSubmitSuccess] = React.useState<boolean | undefined>(undefined);

  React.useEffect(() => {
    setPaperlessEmailAddress(users.activeUser.userEmail);
  }, [users.activeUser]);

  const submitPaperlessService = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    const emailIsValid = validateEmail(paperlessEmailAddress ?? '');

    if (!paperlessService) {
      if (emailIsValid) {
        setPaperlessEmailAddressIsValid(true);
        changeProduct();
      } else {
        setPaperlessEmailAddressIsValid(false);
      }
    } else {
      changeProduct();
    }

    async function changeProduct(): Promise<void> {
      setPaperlessSubmitting(true);
      const productChangeRequest = await postProductChange('contactMethodChange', contract.customerId, users.activeUser.userId, contract.contractNo, {
        newContactMethod: paperlessService ? 'other' : 'electronic',
      });

      if (users.activeUser.userEmail !== paperlessEmailAddress) {
        const detailsToUpdate = {
          number: users.activeUser.userNumber ?? '',
          email: paperlessEmailAddress ?? '',
        };

        await postCustomerDetails(contract.customerId, users.activeUser.userId, detailsToUpdate);
      }

      if (productChangeRequest.status !== 200) {
        setPaperlessSubmitting(false);
        setPaperlessSubmitSuccess(false);
      } else {
        setPaperlessSubmitting(false);
        setPaperlessService(!paperlessService);
        setPaperlessSubmitSuccess(true);
        if (setEligible && setReason) {
          setEligible(false);
          setReason('changedToday');
        }
        setShowPaperlessServiceModal(false);
      }
    }
  };

  return (
    <>
      <Dialog rounded openModal={showPaperlessServiceModal} closeModal={(): void => setShowPaperlessServiceModal(false)}>
        <div className={styles.paperlessDialog}>
          <form onSubmit={submitPaperlessService}>
            <h3>{t(`contracts:paperless.${paperlessService ? `disable` : `enable`}ModalTitle`)}</h3>
            <p>{t(`contracts:paperless.${paperlessService ? `disable` : `enable`}ModalText`)}</p>
            {!paperlessService && (
              <div className={styles.email}>
                <label htmlFor="paperlessEmailAddress">{t(`common:general.emailAddress`)}</label>

                <input
                  type="email"
                  id="paperlessEmailAddress"
                  value={paperlessEmailAddress}
                  onChange={(e) => {
                    setPaperlessEmailAddressIsValid(validateEmail(e.target.value));
                    setPaperlessEmailAddress(e.target.value);
                  }}
                  required
                />
                {paperlessEmailAddressIsValid === false && (
                  <Snackbar type="error" inline>
                    {t(`common:contactform.email`)}
                  </Snackbar>
                )}
              </div>
            )}
            {paperlessSubmitting && <Loading size="small" />}

            {paperlessSubmitSuccess === false && (
              <Snackbar type="error" inline>
                {t(`contracts:paperless.paperlessChangeFailed`)}
              </Snackbar>
            )}

            <div className={styles.buttons}>
              <Button
                type="reset"
                className="buttonSecondary"
                onClick={(): void => {
                  setPaperlessSubmitSuccess(undefined);
                  setShowPaperlessServiceModal(false);
                }}
              >
                {t(`common:general.cancel`)}
              </Button>
              <Button
                type="submit"
                disabled={paperlessEmailAddressIsValid === false}
                onClick={(e): void => {
                  // @ts-ignore
                  if (e.target.ariaDisabled !== 'false') {
                    e.preventDefault();
                  }
                }}
              >
                {t(paperlessService ? `contracts:paperless.disableModalCTA` : `common:general.save`)}
              </Button>
            </div>
          </form>
        </div>
      </Dialog>
      <div className={styles.paperless}>
        <div>
          <h4>{t(`contracts:paperless.title`)}</h4>
          {paperlessService && <p>{t(`contracts:paperless.enabled`)}</p>}
          {!paperlessService && <p>{t(`contracts:paperless.disabled`)}</p>}
        </div>
        <Switch
          offLabel={t(`contracts:paperless.off`)}
          onLabel={t(`contracts:paperless.on`)}
          active={paperlessService}
          disabled={disabled}
          onChange={() => {
            setShowPaperlessServiceModal(true);
          }}
          toggle
        />
      </div>
      {!paperlessSubmitSuccess && disabled && (
        <Snackbar type="info" inline>
          {t(`contracts:paperless.changeAvailableOncePerDay`)}
        </Snackbar>
      )}
      {paperlessSubmitSuccess && (
        <Snackbar type="success" inline>
          {t(`contracts:paperless.confirmation${paperlessService ? `Enabled` : `Disabled`}`)}
        </Snackbar>
      )}
    </>
  );
};

export default PaperlessService;
